import React, { useMemo } from 'react'

import agent from 'agent'
import Button from 'components/common/Button'
import { ModalFooterSection } from 'components/common/ConfirmationModal'
import Hr from 'components/common/Hr'
import Text from 'components/common/Text'
import Modal, {
  ModalCloseButton,
  ModalHeader,
  ModalTitle
} from 'components/Modal'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import {
  BootcampUserCredentials,
  JoinableBootcampTeam,
  JoinBootcampResponse
} from 'types/api/bootcamps/joinable-bootcamp'
import { UserSessionInfo } from 'types/api/user/session'
import { messageFromResponseError } from 'utils/strings'

const DownloadCredentials: React.FC<{
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  credentials: BootcampUserCredentials
  session: UserSessionInfo
}> = ({ setIsOpen, credentials, session }) => {
  const [downloaded, setDownloaded] = React.useState(false)

  const authState = useAuth()
  const credentialsDownloadURL = useMemo(() => {
    const data =
      `Username: ${credentials.username}\n` +
      `Password: ${credentials.password}`
    return URL.createObjectURL(new Blob([data]))
  }, [credentials])

  return (
    <>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          justifyItems: 'start',
          alignItems: 'center',
          gap: theme.space[3]
        }}
      >
        <Text
          data-test-download-bootcamp-credentials
          as="p"
          color="medium"
          css={{
            textAlign: 'center',
            gridColumn: '1 / span 3'
          }}
          weight="normal"
        >
          Download the credentials for your created user in case you need to log
          back in. After downloading, you may click continue to be automatically
          logged into the bootcamp and redirected to the Gremlin home dashboard.
        </Text>
        <Hr
          css={{
            marginBottom: theme.space[3],
            gridColumn: '1 / span 3'
          }}
        />
        <Button
          data-test-download-credentials-download
          secondary
          stretch
          as="a"
          css={{
            gridColumn: '1 / span 3',
            justifyContent: 'center'
          }}
          download="bootcamp-credentials.txt"
          href={credentialsDownloadURL}
          title="Download user credentials for the bootcamp"
          onClick={() => {
            setDownloaded(true)
          }}
        >
          Download
        </Button>
        <Button
          data-test-download-credentials-continue
          primary
          stretch
          css={{
            gridColumn: '1 / span 3'
          }}
          disabled={!downloaded}
          label="Continue"
          onClick={async () => {
            // Set session in local storage
            await authState.startSession(session)

            setIsOpen(false)

            // redirect to home page
            window.location.href = '/'
          }}
        />
      </div>
    </>
  )
}

const JoinBootcampConfirmation: React.FC<{
  bootcampCode: string
  companyId: string
  team: JoinableBootcampTeam
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setCredentials: React.Dispatch<React.SetStateAction<BootcampUserCredentials>>
  setSession: React.Dispatch<React.SetStateAction<UserSessionInfo>>
}> = ({
  bootcampCode,
  companyId,
  team,
  setIsOpen,
  setCredentials,
  setSession
}) => {
  return (
    <>
      <div
        css={{
          marginTop: theme.space[3],
          textAlign: 'center'
        }}
      >
        <Text
          data-test-confirm-join-bootcamp
          as="h4"
          color="medium"
          css={{
            textAlign: 'center'
          }}
          weight="normal"
        >
          You will join the team <b>{team.name}</b> in the bootcamp. An account
          will be automatically created for you in this team.
        </Text>
      </div>
      <ModalFooterSection
        confirmButtonType="primary"
        confirmText="Join"
        onCancel={() => setIsOpen(false)}
        onConfirm={async () => {
          let response
          try {
            response = await agent.post<JoinBootcampResponse>(
              `/bootcamps/${companyId}/join`,
              {
                code: bootcampCode,
                teamId: team.identifier
              }
            )
          } catch (err) {
            toast({
              type: 'error',
              text: messageFromResponseError(
                err,
                'There was an issue joining the bootcamp. Please try again later.'
              )
            })
          }

          setSession((response.data as JoinBootcampResponse).session)
          setCredentials((response.data as JoinBootcampResponse).credentials)
        }}
      />
    </>
  )
}

const JoinBootcampConfirmationModal: React.FC<{
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  bootcampCode: string
  companyId: string
  team: JoinableBootcampTeam
}> = ({ isOpen, setIsOpen, bootcampCode, companyId, team }) => {
  const [credentials, setCredentials] =
    React.useState<BootcampUserCredentials>()
  const [session, setSession] = React.useState<UserSessionInfo>()

  return (
    <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
      <ModalCloseButton
        data-test-close-create-bootcamp-modal
        onClick={() => setIsOpen(false)}
      />
      <ModalHeader>
        {credentials ? (
          <ModalTitle>Download Credentials</ModalTitle>
        ) : (
          <ModalTitle>Join Bootcamp</ModalTitle>
        )}
      </ModalHeader>
      {credentials ? (
        <DownloadCredentials
          credentials={credentials}
          session={session}
          setIsOpen={setIsOpen}
        />
      ) : (
        <JoinBootcampConfirmation
          bootcampCode={bootcampCode}
          companyId={companyId}
          setCredentials={setCredentials}
          setIsOpen={setIsOpen}
          setSession={setSession}
          team={team}
        />
      )}
    </Modal>
  )
}

export default JoinBootcampConfirmationModal
